<!--
 * A Vue component to load an certificate file.
 *
 *
 * Properties
 * ==========
 * - name -
 * (Optional) The text that is used as id and name for all elements.
 *
 * - value -
 * (Optional) To set the initial value of the input element.
 *
 * Events
 * ==========
 * - input -
 * This event is sent when user changes the input value.
 * The object to sent along with this event is a certificate file byte array.
 *
-->
<template>
    <div>
        <!--esLint complains about localCertStatus, see explanation by watch-->
        <x-input :id="`certificate-loader-${name}`"
                 type="text"
                 required
                 v-model="localCertStatus"
                 :disabled="true"
                 :maxlength="50">
        </x-input>
        <b-form-checkbox v-show="!showFileSelection"
                         :id="`checkbox-${name}`"
                         :name="`checkbox-${name}`"
                         @change="$_onSelectChanged">
            {{selectOtherCertificateTitle}}
        </b-form-checkbox>
        <input v-show="showFileSelection"
               type="file"
               ref="certificateloaderinput"
               :id="`certificate-loader-input-${name}`"
               :name="`certificate-loader-input-${name}`"
               accept=".pfx"
               @change="$_onChange">
    </div>
</template>

<script>
    import modalMixin from '../components/Mixins/ModalMixin.vue';
    import XInput from '../components/BaseComponents/x-input.vue';

    export default {
        inheritAttrs: false,
        name: 'CertificateLoader',

        mixins: [
            modalMixin
        ],

        components: { 'x-input': XInput },

        props: {
            value: { type: String, default: '' },
            name: { type: String, default: '' },
            certStatus: { type: String, default: '' },
            certSelected: { type: Boolean, default: false },
            required: { type: Boolean, default: false }
        },

        data() {
            return {
                selectOtherCertificateTitle: this.$t('apiSettings.selectOtherCertificateTitle'),
                showFileSelectionChecked: false,
                certificateData: [],
                fileReader: null,
                fileMetadata: {
                    fileName: '',
                    fileType: ''
                },
                localCertStatus: null
            };
        },

        computed: {
            showFileSelection: function () {
                return !this.certSelected || this.showFileSelectionChecked;
            },

            fileData: {
                get() {
                    return this.certificateData;
                },

                set(input) {
                    this.certificateData = input;
                    this.$emit('input', input);
                    this.$emit('metadata', this.fileMetadata);
                    return input;
                }
            }
        },

        methods: {
            $_onSelectChanged () {
                this.showFileSelectionChecked = true;
                this.$_removeFile();
                this.$emit('loadFileValidState', false);
            },

            /*
            * Creates a filereader object and subscribe to onload event.
            */
            $_initFileReader() {
                this.fileReader = new FileReader();
                this.fileReader.onload = (e) => {
                    this.fileData = new Uint8Array(e.target.result);
                };
            },

            /*
            * Starts the process of loading image
            * @param {event} input event which contain the image data
            */
            $_onChange(event) {
                const input = event.target;
                if (input.value) {
                    if (this.$_validateFile(input)) {
                        this.$_initFileReader();
                        this.fileMetadata.fileName = input.files[0].name;
                        this.fileMetadata.fileType = input.files[0].type;
                        this.fileReader.readAsArrayBuffer(input.files[0]);
                    }
                } else {
                    this.$_removeFile();
                }
            },

            /*
            * Removes the loaded image
            */
            $_removeFile() {
                this.fileData = '';
                // Reset the value so that the same file can be selected again
                this.$refs.certificateloaderinput.value = '';
            },

            /*
            * Validate file selected
            * @param {Object} image - The image to be validated, properties to validate on: image.filename, image.filetype & image.filesize
            * @returns {bool} returns boolean value indicating if the image is valid.
            */
            $_validateFile(input) {
                let valid = true;
                let certErrorMessage;
                if (!input.files) {
                    valid = false;
                    certErrorMessage = 'loadFileErrNoFileIsChosen';
                } else if (!input.files[0]) {
                    valid = false;
                    certErrorMessage = 'loadFileErrFileNoContent';
                }
                if (!valid) {
                    this.$_removeImage();
                    this.showToast({
                        message: this.$t(certErrorMessage),
                        variant: 'danger',
                        autoHideDelay: 5000,
                        noCloseButton: true
                    });
                }
                // Send validation state to the subscribers
                this.$emit('loadFileValidState', valid);
                return valid;
            }
        },
        watch: {
            // Here is a watch on certStatus added, then EsLiont doesnt complain
            // In fact certStatus is used only as input variable, doesnt
            // change in x-input (two states)
            certStatus: {
                handler: function (newVal) {
                    this.localCertStatus = newVal.slice(0);
                },
                deep: true,
                immediate: true
            }
        }
    };
</script>
